import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiFetch } from '../utils/httpClient';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faPlus,
  faSpinner,
  faCircleCheck,
  faBuilding,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';

const Section = styled.section`
  margin-bottom: var(--spacing-10);
  width: 100%;
  min-width: 0;
  overflow-x: visible;
  max-width: none;
  margin-left: 0;
  position: relative;
`;

const SectionTitle = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: var(--spacing-2);
  color: var(--color-text-primary);
  text-align: left;
`;

const SectionDescription = styled.p`
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-8);
  font-size: 0.95rem;
  text-align: left;
`;

const ControlsBar = styled.div`
  display: flex;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-3) var(--spacing-5);
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s var(--transition-smooth);

  &:hover {
    background: var(--color-accent-light);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SearchInput = styled.input`
  padding: var(--spacing-3) var(--spacing-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  width: 300px;
  min-width: 150px;
  flex: 1;
  max-width: 100%;

  @media (max-width: 30rem) { /* --breakpoint-mobile */
    width: 100%;
    min-width: 120px;
    padding: var(--spacing-2) var(--spacing-3);
    font-size: 0.875rem;
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const OrganizationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: var(--spacing-6);
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    grid-template-columns: 1fr;
    gap: var(--spacing-4);
  }
`;

const OrganizationCard = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--spacing-6);
  transition: all 0.2s var(--transition-smooth);

  &:hover {
    border-color: var(--color-accent);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const OrganizationName = styled.h3`
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-2);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);

  svg {
    color: var(--color-accent);
  }
`;

const OrganizationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  margin-top: var(--spacing-4);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
  gap: var(--spacing-4);
  min-width: 0;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    gap: var(--spacing-2);
    font-size: 0.875rem;
  }
  
  span:first-child {
    flex-shrink: 0;
    font-weight: 500;
  }
  
  span:last-child {
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1;
  }
`;

const Badge = styled.span<{ $variant?: 'success' | 'warning' | 'error' }>`
  padding: var(--spacing-1) var(--spacing-3);
  border-radius: var(--radius-pill);
  font-size: var(--font-size-xs);
  font-weight: 600;
  background: ${props => {
    switch (props.$variant) {
      case 'success': return 'rgba(16, 185, 129, 0.12)';
      case 'warning': return 'rgba(250, 204, 21, 0.12)';
      case 'error': return 'rgba(239, 68, 68, 0.12)';
      default: return 'var(--color-surface-alt)';
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'success': return '#10b981';
      case 'warning': return '#facc15';
      case 'error': return '#ef4444';
      default: return 'var(--color-text-primary)';
    }
  }};
`;

const Modal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--spacing-8);
  min-width: 500px;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-6);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  margin-bottom: var(--spacing-4);
`;

const Label = styled.label`
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--color-text-primary);
`;

const Input = styled.input`
  padding: var(--spacing-3) var(--spacing-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface-alt);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: var(--spacing-3) var(--spacing-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface-alt);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-3);
  justify-content: flex-end;
  margin-top: var(--spacing-6);
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: var(--spacing-3) var(--spacing-5);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;

  ${props => props.$variant === 'primary' ? `
    background: var(--color-accent);
    color: #fff;
    border-color: var(--color-accent);
    &:hover:not(:disabled) {
      background: var(--color-accent-light);
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  ` : `
    background: var(--color-surface);
    color: var(--color-text-primary);
    &:hover {
      background: var(--color-surface-alt);
    }
  `}
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-3);
  padding: var(--spacing-4) var(--spacing-5);
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(5, 150, 105, 0.08));
  border: 1px solid rgba(16, 185, 129, 0.25);
  border-radius: var(--radius-lg);
  color: #6ee7b7;
  margin-bottom: var(--spacing-4);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-relaxed);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
  backdrop-filter: blur(8px);

  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
    color: #10b981;
    font-size: 1.125rem;
  }
`;

const ErrorState = styled.div`
  padding: var(--spacing-4);
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: var(--radius-md);
  color: #ff8080;
  margin-bottom: var(--spacing-4);
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-12);
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
`;

const OrgListContainer = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  margin: var(--spacing-4) 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-alt);
`;

const OrgListItem = styled.div<{ $selected?: boolean; $disabled?: boolean }>`
  padding: var(--spacing-4);
  border-bottom: 1px solid var(--color-border);
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  background: ${props => props.$selected ? 'rgba(239, 68, 68, 0.1)' : 'transparent'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: all 0.2s ease;

  &:hover:not([disabled]) {
    background: ${props => props.$selected ? 'rgba(239, 68, 68, 0.15)' : 'var(--color-surface)'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const OrgListItemName = styled.div`
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-1);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
`;

const OrgListItemDetails = styled.div`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  margin-top: var(--spacing-1);
`;

const OrgListEmpty = styled.div`
  padding: var(--spacing-8);
  text-align: center;
  color: var(--color-text-tertiary);
`;

interface Organization {
  id: string;
  name: string;
  organization_type: string;
  leagues: string[];
  opponents_leagues: string[];
  team_id?: string;
  status: string;
  subscription_tier: string;
  max_users: number;
  current_users: number;
  created_at: string;
}

export const OrganizationManagementSection: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChooseOrgModal, setShowChooseOrgModal] = useState(false);
  const [orgCreated, setOrgCreated] = useState(false);
  const [addingOrg, setAddingOrg] = useState(false);

  // Global mappings organizations state
  const [globalOrgs, setGlobalOrgs] = useState<any[]>([]);
  const [loadingGlobalOrgs, setLoadingGlobalOrgs] = useState(false);
  const [globalOrgsSearch, setGlobalOrgsSearch] = useState('');
  const [globalOrgsSkip, setGlobalOrgsSkip] = useState(0);
  const [globalOrgsTotal, setGlobalOrgsTotal] = useState(0);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  const fetchOrganizations = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch('/api/v1/organizations/admin/organizations?skip=0&limit=1000');

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      setOrganizations(data.organizations || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchGlobalMappingsOrganizations = async (search: string = '', skip: number = 0) => {
    setLoadingGlobalOrgs(true);
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await apiFetch(
        `/api/v1/organizations/admin/global-mappings?skip=${skip}&limit=50${searchParam}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch organizations from global_mappings');
      }

      const data = await response.json();
      setGlobalOrgs(data.organizations || []);
      setGlobalOrgsTotal(data.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoadingGlobalOrgs(false);
    }
  };

  const handleOpenChooseOrgModal = () => {
    setShowChooseOrgModal(true);
    setGlobalOrgsSearch('');
    setGlobalOrgsSkip(0);
    setSelectedTeamId(null);
    fetchGlobalMappingsOrganizations('', 0);
  };

  const handleCloseChooseOrgModal = () => {
    setShowChooseOrgModal(false);
    setGlobalOrgsSearch('');
    setGlobalOrgsSkip(0);
    setSelectedTeamId(null);
    setOrgCreated(false);
    setError(null);
  };

  const handleSelectOrganization = (teamId: string) => {
    setSelectedTeamId(teamId);
  };

  const handleImportSelectedOrganization = async () => {
    if (!selectedTeamId) {
      window.alert('Please select an organization');
      return;
    }

    setAddingOrg(true);
    setError(null);

    try {
      const response = await apiFetch('/api/v1/organizations/admin/organizations/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          team_id: selectedTeamId,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.detail || 'Failed to import organization');
      }

      setOrgCreated(true);
      await fetchOrganizations();
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseChooseOrgModal();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import organization');
      window.alert(err instanceof Error ? err.message : 'Failed to import organization');
    } finally {
      setAddingOrg(false);
    }
  };

  const handleGlobalOrgsSearch = (search: string) => {
    setGlobalOrgsSearch(search);
    setGlobalOrgsSkip(0);
    fetchGlobalMappingsOrganizations(search, 0);
  };

  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <Badge $variant="success">Active</Badge>;
      case 'inactive':
        return <Badge $variant="error">Inactive</Badge>;
      case 'suspended':
        return <Badge $variant="warning">Suspended</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Only system admins can access this section
  if (currentUser?.role !== 'system_admin' && currentUser?.role !== 'admin') {
    return (
      <Section>
        <SectionTitle>Organization Management</SectionTitle>
        <ErrorState>Only system administrators can manage organizations.</ErrorState>
      </Section>
    );
  }

  return (
    <Section>
      <SectionTitle>Organization Management</SectionTitle>
      <SectionDescription>Manage organizations and their settings</SectionDescription>

      <ControlsBar>
        <SearchInput
          type="text"
          placeholder="Search organizations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <AddButton onClick={handleOpenChooseOrgModal}>
          <FontAwesomeIcon icon={faPlus} />
          Add Organization
        </AddButton>
      </ControlsBar>

      {loading ? (
        <LoadingState>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 'var(--spacing-2)' }} />
          Loading organizations...
        </LoadingState>
      ) : error ? (
        <ErrorState>{error}</ErrorState>
      ) : (
        <OrganizationGrid>
          {filteredOrgs.map((org) => (
            <OrganizationCard key={org.id}>
              <OrganizationName>
                <FontAwesomeIcon icon={faBuilding} />
                {org.name}
              </OrganizationName>

              <OrganizationInfo>
                <InfoRow>
                  <span>Type:</span>
                  <span>{org.organization_type}</span>
                </InfoRow>

                <InfoRow>
                  <span>Status:</span>
                  <span>{getStatusBadge(org.status)}</span>
                </InfoRow>

                <InfoRow>
                  <span>Users:</span>
                  <span>
                    <FontAwesomeIcon icon={faUsers} style={{ marginRight: 'var(--spacing-1)' }} />
                    {org.current_users} / {org.max_users}
                  </span>
                </InfoRow>

                {org.leagues && org.leagues.length > 0 && (
                  <InfoRow>
                    <span>League:</span>
                    <span>{org.leagues.join(', ')}</span>
                  </InfoRow>
                )}

                {org.opponents_leagues && org.opponents_leagues.length > 0 && (
                  <InfoRow>
                    <span>Opponent Leagues:</span>
                    <span>{org.opponents_leagues.join(', ')}</span>
                  </InfoRow>
                )}

                {org.team_id && (
                  <InfoRow>
                    <span>Team ID:</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{org.team_id}</span>
                  </InfoRow>
                )}

                <InfoRow>
                  <span>Subscription:</span>
                  <span><Badge>{org.subscription_tier}</Badge></span>
                </InfoRow>
              </OrganizationInfo>
            </OrganizationCard>
          ))}
        </OrganizationGrid>
      )}

      <Modal $isOpen={showChooseOrgModal} onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleCloseChooseOrgModal();
        }
      }}>
        <ModalContent onClick={(e) => e.stopPropagation()} style={{ minWidth: '600px', maxWidth: '800px' }}>
          <ModalTitle>Add Organization</ModalTitle>

          {orgCreated ? (
            <>
              <SuccessMessage>
                <FontAwesomeIcon icon={faCircleCheck} />
                <div>Organization imported successfully!</div>
              </SuccessMessage>

              <ModalButtonGroup>
                <ModalButton onClick={handleCloseChooseOrgModal} $variant="primary">
                  Done
                </ModalButton>
              </ModalButtonGroup>
            </>
          ) : (
            <>
              <FormGroup>
                <Label>Search Organizations</Label>
                <Input
                  type="text"
                  placeholder="Search by name or team ID..."
                  value={globalOrgsSearch}
                  onChange={(e) => handleGlobalOrgsSearch(e.target.value)}
                  disabled={addingOrg || loadingGlobalOrgs}
                />
              </FormGroup>

              <OrgListContainer>
                {loadingGlobalOrgs ? (
                  <OrgListEmpty>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 'var(--spacing-2)' }} />
                    Loading organizations...
                  </OrgListEmpty>
                ) : globalOrgs.length === 0 ? (
                  <OrgListEmpty>
                    {globalOrgsSearch ? 'No organizations found matching your search.' : 'No organizations available.'}
                  </OrgListEmpty>
                ) : (
                  globalOrgs.map((org) => (
                    <OrgListItem
                      key={org.team_id}
                      $selected={selectedTeamId === org.team_id}
                      $disabled={org.already_imported}
                      onClick={() => !org.already_imported && handleSelectOrganization(org.team_id)}
                    >
                      <OrgListItemName>
                        <FontAwesomeIcon icon={faBuilding} />
                        {org.name}
                        {org.already_imported && (
                          <Badge $variant="warning" style={{ marginLeft: 'auto' }}>
                            Already Imported
                          </Badge>
                        )}
                      </OrgListItemName>
                      <OrgListItemDetails>
                        <div>Team ID: <code style={{ fontSize: '0.85em' }}>{org.team_id}</code></div>
                        {org.organization_type && (
                          <div>Type: {org.organization_type}</div>
                        )}
                        {org.opponents_leagues && org.opponents_leagues.length > 0 && (
                          <div>Leagues: {org.opponents_leagues.join(', ')}</div>
                        )}
                      </OrgListItemDetails>
                    </OrgListItem>
                  ))
                )}
              </OrgListContainer>

              {globalOrgsTotal > 50 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 'var(--spacing-2)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-tertiary)'
                }}>
                  <span>Showing {globalOrgsSkip + 1}-{Math.min(globalOrgsSkip + 50, globalOrgsTotal)} of {globalOrgsTotal}</span>
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                    <ModalButton
                      onClick={() => {
                        const newSkip = Math.max(0, globalOrgsSkip - 50);
                        setGlobalOrgsSkip(newSkip);
                        fetchGlobalMappingsOrganizations(globalOrgsSearch, newSkip);
                      }}
                      disabled={globalOrgsSkip === 0 || loadingGlobalOrgs}
                      style={{ padding: 'var(--spacing-1) var(--spacing-3)', fontSize: 'var(--font-size-xs)' }}
                    >
                      Previous
                    </ModalButton>
                    <ModalButton
                      onClick={() => {
                        const newSkip = globalOrgsSkip + 50;
                        setGlobalOrgsSkip(newSkip);
                        fetchGlobalMappingsOrganizations(globalOrgsSearch, newSkip);
                      }}
                      disabled={globalOrgsSkip + 50 >= globalOrgsTotal || loadingGlobalOrgs}
                      style={{ padding: 'var(--spacing-1) var(--spacing-3)', fontSize: 'var(--font-size-xs)' }}
                    >
                      Next
                    </ModalButton>
                  </div>
                </div>
              )}

              {error && (
                <ErrorState style={{ marginTop: 'var(--spacing-4)' }}>{error}</ErrorState>
              )}

              <ModalButtonGroup>
                <ModalButton onClick={handleCloseChooseOrgModal} disabled={addingOrg}>
                  Cancel
                </ModalButton>
                <ModalButton
                  onClick={handleImportSelectedOrganization}
                  $variant="primary"
                  disabled={!selectedTeamId || addingOrg}
                >
                  {addingOrg ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 'var(--spacing-2)' }} />
                      Importing...
                    </>
                  ) : (
                    'Import Selected'
                  )}
                </ModalButton>
              </ModalButtonGroup>
            </>
          )}
        </ModalContent>
      </Modal>
    </Section>
  );
};

